import { format, addDay } from '@formkit/tempo'
import { normalizeBookingWindowDays, bookingWindowEndKey, dayKeyToLocalDate } from '~/utils/bookingWindow'

export function shouldDisableDate(
  date: Date,
  minDate?: Date,
  maxDate?: Date,
  unavailableDates?: Set<string> | string[]
): boolean {
  if (!isValidDate(date)) return false
  if (minDate && isValidDate(minDate) && date < startOfDay(minDate)) return true
  if (maxDate && isValidDate(maxDate) && date > endOfDay(maxDate)) return true

  if (unavailableDates && containsDate(unavailableDates, date)) return true
  return false
}

/**
 * Calendar bounds from the available slots.
 * `bookingWindowDays` (runtimeConfig.public.bookingWindowDays) caps maxDate at
 * UTC today + N days inclusive, the same day key the server uses; 0/undefined
 * means no cap.
 */
export function computeBoundsFromSlots(
  slots: Array<{ TimeSlotStart: string; TimeSlotEnd?: string }>,
  bookingWindowDays?: unknown
) {
  const today = startOfDay(new Date())
  const endKey = bookingWindowEndKey(normalizeBookingWindowDays(bookingWindowDays))
  const windowEnd = endKey ? dayKeyToLocalDate(endKey) : undefined
  const capToWindow = (max: Date) => (windowEnd && max > windowEnd ? windowEnd : max)

  // Fallback window when no slots are available
  const fallback = {
    minDate: addDay(today, 1),
    maxDate: capToWindow(addDay(today, 365))
  }
  if (!slots || slots.length === 0) return fallback

  // Earliest start day among all slots
  const startDays: Date[] = slots
    .map(s => startOfDay(new Date(s.TimeSlotStart)))
    .filter(d => isValidDate(d))

  // Latest end day among all slots (fallback to start if end missing)
  const endDays: Date[] = slots
    .map(s => startOfDay(new Date(s.TimeSlotEnd || s.TimeSlotStart)))
    .filter(d => isValidDate(d))

  if (startDays.length === 0 || endDays.length === 0) return fallback

  const min = new Date(Math.min(...startDays.map(d => d.getTime())))
  const max = capToWindow(new Date(Math.max(...endDays.map(d => d.getTime()))))

  // Every slot lies past the window (server and client disagree on "today", or
  // the config differs): treat it as no slots rather than returning min > max,
  // which would disable every day while still preselecting an out-of-window one.
  if (min > max) return fallback

  // Return exact day bounds; shouldDisableDate uses endOfDay(max) for inclusive check
  return { minDate: min, maxDate: max }
}

export function toUnavailableSet(unavailableDates: string[] | Set<string>) {
  return unavailableDates instanceof Set ? unavailableDates : new Set(unavailableDates)
}

function containsDate(list: Set<string> | string[], date: Date) {
  if (!isValidDate(date)) return false
  const sod = startOfDay(date)
  if (!isValidDate(sod)) return false
  const key = format(sod, 'YYYY-MM-DD')
  if (list instanceof Set) return list.has(key)
  return list.includes(key)
}

function startOfDay(d: Date) {
  if (!isValidDate(d)) return d as unknown as Date
  const date = new Date(d)
  date.setHours(0, 0, 0, 0)
  return date
}

function endOfDay(d: Date) {
  if (!isValidDate(d)) return d as unknown as Date
  const date = new Date(d)
  date.setHours(23, 59, 59, 999)
  return date
}

function isValidDate(d: any): d is Date {
  return d instanceof Date && !isNaN(d.getTime())
}
